import { useState, useEffect, useCallback } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Shield, Lock, X, Plus, AlertTriangle, AlertCircle } from 'lucide-react'
import {
  listRoles,
  getUserRoles,
  getEffectivePermissions,
  addRoleMember,
  removeRoleMember,
  type Role,
  type EffectivePermissionGroup,
} from '../lib/api'
import { PickerDropdown } from './PickerDropdown'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { roleBadgeClasses } from '../lib/roleColors'

const ADMIN_ROLE = 'Admin'

interface TargetUser {
  id: number
  email: string
  name?: string | null
}

interface ManageUserRolesModalProps {
  user: TargetUser | null
  onClose: () => void
  onChanged?: () => void
}

/** Per-user role editor + effective-permissions view, opened by clicking a
 * user's role badge in the Users list. Lazily loads when a user is set. */
export function ManageUserRolesModal({ user, onClose, onChanged }: ManageUserRolesModalProps) {
  const [allRoles, setAllRoles] = useState<Role[]>([])
  const [assigned, setAssigned] = useState<Role[]>([])
  const [effective, setEffective] = useState<EffectivePermissionGroup[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Pending Admin-role grant/revoke awaiting confirmation (high-stakes; everything
  // else applies immediately). null = no confirmation open.
  const [pendingAdmin, setPendingAdmin] = useState<{ action: 'add' | 'remove'; roleId: number } | null>(null)

  const load = useCallback(async () => {
    if (!user) return
    setError(null)
    try {
      const [rolesResp, userRolesResp, effResp] = await Promise.all([
        listRoles(),
        getUserRoles(user.id),
        getEffectivePermissions(user.id),
      ])
      setAllRoles(rolesResp.roles)
      setAssigned(userRolesResp.roles)
      setEffective(effResp.effectivePermissions)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load roles')
    }
  }, [user])

  useEffect(() => {
    if (user) load()
  }, [user, load])

  const isAdminRole = (roleId: number) =>
    allRoles.some((r) => r.id === roleId && r.name === ADMIN_ROLE)

  function addRole(roleId: number) {
    if (isAdminRole(roleId)) {
      setPendingAdmin({ action: 'add', roleId })
      return
    }
    void doAddRole(roleId)
  }

  function removeRole(roleId: number) {
    if (isAdminRole(roleId)) {
      setPendingAdmin({ action: 'remove', roleId })
      return
    }
    void doRemoveRole(roleId)
  }

  async function doAddRole(roleId: number) {
    if (!user) return
    setBusy(true)
    setError(null)
    try {
      await addRoleMember(roleId, user.id)
      await load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add role')
    } finally {
      setBusy(false)
    }
  }

  async function doRemoveRole(roleId: number) {
    if (!user) return
    setBusy(true)
    setError(null)
    try {
      await removeRoleMember(roleId, user.id)
      await load()
      onChanged?.()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove role')
    } finally {
      setBusy(false)
    }
  }

  // Member is the implicit baseline (everyone has it) — manage only the extra roles.
  const extraRoles = assigned.filter((r) => r.name !== 'Member')

  return (
    <Dialog.Root open={user !== null} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92vw] max-w-lg max-h-[88vh] overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5" /> Roles
            </Dialog.Title>
            <Dialog.Close className="text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-sm text-muted-foreground mb-4 truncate">
            {user?.name ? `${user.name} · ` : ''}{user?.email}
          </Dialog.Description>

          {error && (
            <div className="mb-3 p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20 flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm font-medium">Assigned roles</span>
            <PickerDropdown
              label="Add role"
              icon={<Plus className="w-3 h-3" />}
              disabled={busy}
              emptyText="All roles assigned"
              options={allRoles
                .filter((r) => r.name !== 'Member' && !assigned.some((a) => a.id === r.id))
                .map((r) => ({ id: r.id, label: r.name }))}
              onPick={addRole}
            />
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {extraRoles.map((role) => (
              <span
                key={role.id}
                className={`flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-sm border ${roleBadgeClasses(role)}`}
              >
                {role.isSystem && <Lock className="w-3 h-3" />}
                {role.name}
                <button
                  type="button"
                  aria-label={`Remove ${role.name}`}
                  disabled={busy}
                  onClick={() => removeRole(role.id)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {extraRoles.length === 0 && (
              <p className="text-sm text-muted-foreground">No extra roles.</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-6">
            Every user is a <span className="font-medium">Member</span> by default. Granting
            {' '}<span className="font-medium">Admin</span> gives full platform access.
          </p>

          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-medium mb-2">Effective permissions</h3>
            {effective.length === 0 ? (
              <p className="text-sm text-muted-foreground">No admin-area permissions (regular user access).</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {effective.map((group) => (
                  <div key={group.category}>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-1">{group.category}</h4>
                    <ul className="space-y-0.5">
                      {group.permissions.map((perm) => (
                        <li key={perm.key} className="text-sm flex items-center gap-1.5">
                          {perm.description || perm.key}
                          {perm.sensitive && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                              <AlertTriangle className="w-2.5 h-2.5" /> sensitive
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>

          <ConfirmDialog
            open={pendingAdmin !== null}
            onOpenChange={(o) => !o && setPendingAdmin(null)}
            title={pendingAdmin?.action === 'remove' ? 'Remove Admin access?' : 'Grant Admin access?'}
            description={
              pendingAdmin?.action === 'remove'
                ? `This removes full platform-admin access from ${user?.email}.`
                : `This grants ${user?.email} the Admin role — full access to every part of the platform.`
            }
            confirmLabel={pendingAdmin?.action === 'remove' ? 'Remove Admin' : 'Grant Admin'}
            variant={pendingAdmin?.action === 'remove' ? 'destructive' : 'default'}
            onConfirm={async () => {
              if (!pendingAdmin) return
              if (pendingAdmin.action === 'add') await doAddRole(pendingAdmin.roleId)
              else await doRemoveRole(pendingAdmin.roleId)
              setPendingAdmin(null)
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default ManageUserRolesModal
